import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  Banner,
  Spinner
} from '@shopify/polaris';

export const Login: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If user is already authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Handle automatic authentication
    handleAutomaticAuth();
  }, [isAuthenticated, navigate]);

  // Preserve URL parameters when redirecting to login
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.toString()) {
      // Store URL parameters in sessionStorage to preserve them
      sessionStorage.setItem('login_url_params', urlParams.toString());
    }
  }, []);

  const handleAutomaticAuth = async () => {
    try {
      setIsProcessing(true);
      setError(null);

      // Check if we're in Shopify admin context
      if (authService.isInShopifyAdmin()) {
        const shop = authService.extractShopFromUrl();
        
        if (!shop) {
          setError('Unable to determine shop from URL');
          setIsProcessing(false);
          return;
        }

        // Check if we have embedded parameters
        if (authService.hasEmbeddedParams()) {
          const embeddedParams = authService.getEmbeddedParams();
          const response = await login(shop, 'embedded', embeddedParams);
          
          if (response.success) {
            // Authentication successful, redirect will happen automatically
            return;
          } else if (response.needsOAuth) {
            // Need OAuth flow, show message
            setError('OAuth authentication required. Please complete the authentication in the new window.');
            setIsProcessing(false);
            return;
          } else {
            setError(response.error || 'Embedded authentication failed');
            setIsProcessing(false);
            return;
          }
        } else {
          // No embedded parameters, try OAuth flow
          const response = await login(shop, 'oauth');
          
          if (response.success && response.oauthUrl) {
            // OAuth URL provided, show message
            setError('OAuth authentication required. Please complete the authentication in the new window.');
            setIsProcessing(false);
            return;
          } else {
            setError(response.error || 'OAuth initiation failed');
            setIsProcessing(false);
            return;
          }
        }
      } else {
        // Not in Shopify admin, show manual login
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Automatic authentication error:', error);
      setError('Authentication failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleManualLogin = async () => {
    try {
      setIsProcessing(true);
      setError(null);

      const shop = prompt('Please enter your Shopify store domain (e.g., your-store.myshopify.com):');
      
      if (!shop) {
        setIsProcessing(false);
        return;
      }

      if (!shop.match(/^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/)) {
        setError('Invalid shop URL format. Please use format: your-store.myshopify.com');
        setIsProcessing(false);
        return;
      }

      const response = await login(shop, 'oauth');
      
      if (response.success && response.oauthUrl) {
        // OAuth URL provided, show message
        setError('OAuth authentication required. Please complete the authentication in the new window.');
      } else {
        setError(response.error || 'Authentication failed');
      }
    } catch (error) {
      console.error('Manual login error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <div style={{ marginTop: '1rem' }}>
                  <Text variant="bodyMd" as="p">
                    Processing authentication...
                  </Text>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page>
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Text variant="headingLg" as="h1">
                Welcome to TranslateFlow
              </Text>
              
              <div style={{ marginTop: '1rem', marginBottom: '2rem' }}>
                <Text variant="bodyMd" as="p">
                  Connect your Shopify store to start translating your content.
                </Text>
              </div>

              {error && (
                <div style={{ marginBottom: '1rem' }}>
                  <Banner tone="critical">
                    <p>{error}</p>
                  </Banner>
                </div>
              )}

              <Button
                variant="primary"
                size="large"
                onClick={handleManualLogin}
                disabled={isProcessing}
              >
                Connect Shopify Store
              </Button>

              <div style={{ marginTop: '1rem' }}>
                <Text variant="bodySm" as="p" tone="subdued">
                  You'll be redirected to Shopify to authorize the app.
                </Text>
              </div>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}; 